import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import User from "../../Models/User";

export interface AuthGlobalState {
    user?: User;
    need2fa: boolean;
    authLoading: boolean;
    isAuthenticated: boolean;
    userLoginLoading: boolean;
    loginError?: string;
    authChecking?: boolean;
}

const initialState: AuthGlobalState = {
    user: undefined,
    need2fa: false,
    authLoading: false,
    isAuthenticated: false,
    userLoginLoading: false,
    loginError: undefined,
    authChecking: false,
};

const Auth = createSlice({
    name: "auth",
    initialState,
    reducers: {
        VerifyTokenStarted(state, action: PayloadAction<{ silent?: boolean }>) {
            if (!action.payload.silent) {
                state.user = undefined;
                state.isAuthenticated = false;
                state.authLoading = false;
                state.loginError = undefined;
            } else {
                state.authLoading = true;
            }
        },

        VerifyTokenEnd(state) {
            state.authLoading = false;
        },

        AuthenticationStarted(state) {
            state.userLoginLoading = true;
            state.loginError = undefined;
        },

        AuthenticationFailure(state, action: PayloadAction<{ error: string }>) {
            state.loginError = action.payload.error;
            state.userLoginLoading = false;
            state.isAuthenticated = false;
        },
        loggedWithSuccess(
            state,
            action: PayloadAction<{
                user: any;
            }>,
        ) {
            const {
                uid,
                reinvest_auto,
                hide_zero_amount,
                hide_not_active_earning,
                last_name,
                first_name,
                username,
                email,
                scopes,
                photo,
                parrain,
                haveTransactionPassword,
                cap,
                cag,
                is_active,
                is_fiat,
                type,
                entreprise,
                siret,
                phone,
                phoneCountryCode,
                inscriptionCompleted,
                phoneVerified,
                isFromVendo,
                clisha_level,
                date_of_birth,
            } = action.payload.user;

            const authenticatedUser = new User(
                uid,
                username,
                last_name,
                first_name,
                email,
                reinvest_auto,
                hide_zero_amount,
                phone,
                phoneCountryCode,
                inscriptionCompleted,
                phoneVerified,
                isFromVendo,
                clisha_level,
                date_of_birth,
            );

            authenticatedUser.scopes = scopes;
            authenticatedUser.photo = photo;
            authenticatedUser.parrain = parrain;
            authenticatedUser.cag = cag;
            authenticatedUser.cap = cap;
            authenticatedUser.is_active = is_active;
            authenticatedUser.hide_not_active_earning = hide_not_active_earning;
            authenticatedUser.is_fiat = is_fiat;
            authenticatedUser.haveTransactionPassword = haveTransactionPassword;
            authenticatedUser.type = type;
            authenticatedUser.siret = siret;
            authenticatedUser.entreprise = entreprise;

            state.user = authenticatedUser;
            state.isAuthenticated = true;
            state.authLoading = false;
            state.userLoginLoading = false;
            if (typeof localStorage !== "undefined")
                localStorage.setItem("AccountConnected", "yes");
        },
        verifyUserSuccess(state) {
            state.authLoading = false;
            state.userLoginLoading = false;
        },
        Logout(state) {
            state.user = undefined;
            state.isAuthenticated = false;
            state.authLoading = false;
            state.loginError = undefined;
            if (typeof localStorage !== "undefined")
                localStorage.setItem("AccountConnected", "false");
        },
        StartAuthChecking(state) {
            state.authChecking = true;
        },
        EndAuthChecking(state) {
            state.authChecking = false;
        },
        ChangeTwoFANeeded(
            state,
            action: PayloadAction<{
                two_fa_needed: boolean;
            }>,
        ) {
            state.need2fa = action.payload.two_fa_needed;
        },
        UpdateUserInformation(
            state,
            action: PayloadAction<{
                user: any;
            }>,
        ) {
            const {
                uid,
                reinvest_auto,
                hide_zero_amount,
                hide_not_active_earning,
                last_name,
                first_name,
                username,
                email,
                scopes,
                photo,
                parrain,
                haveTransactionPassword,
                cap,
                cag,
                is_active,
                is_fiat,
                type,
                entreprise,
                siret,
                phone,
                phoneCountryCode,
                inscriptionCompleted,
                phoneVerified,
                isFromVendo,
                clisha_level,
                date_of_birth,
            } = action.payload.user;

            const authenticatedUser = new User(
                uid,
                username,
                last_name,
                first_name,
                email,
                reinvest_auto,
                hide_zero_amount,
                phone,
                phoneCountryCode,
                inscriptionCompleted,
                phoneVerified,
                isFromVendo,
                clisha_level,
                date_of_birth,
            );

            authenticatedUser.scopes = scopes;
            authenticatedUser.photo = photo;
            authenticatedUser.parrain = parrain;
            authenticatedUser.cag = cag;
            authenticatedUser.cap = cap;
            authenticatedUser.is_active = is_active;
            authenticatedUser.hide_not_active_earning = hide_not_active_earning;
            authenticatedUser.is_fiat = is_fiat;
            authenticatedUser.haveTransactionPassword = haveTransactionPassword;
            authenticatedUser.type = type;
            authenticatedUser.siret = siret;
            authenticatedUser.entreprise = entreprise;

            state.user = authenticatedUser;
            state.isAuthenticated = true;
            localStorage.setItem("AccountConnected", "yes");
        },
    },
});

export const {
    VerifyTokenStarted,
    VerifyTokenEnd,
    AuthenticationStarted,
    AuthenticationFailure,
    Logout,
    verifyUserSuccess,
    loggedWithSuccess,
    StartAuthChecking,
    EndAuthChecking,
    UpdateUserInformation,
    ChangeTwoFANeeded,
} = Auth.actions;

export default Auth.reducer;
